import { useContext, useEffect, useState } from 'react';
import { toast } from '../../util/toasts';
import { LicenseScan } from './license-scan';
import { TenantContext } from '../../context/tenant-context';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { Typography } from '../../components/typography/typography';
import { UserDocument, UserDocumentType, UserDriverLic } from '../../clients/quipli-service/user/user';
import { EcommerceButton } from '../../components/form/button';
import { Card } from '../../components/card/card';
import { LicenseDetails } from './license-details';
import { Spinner } from '../../components/spinner/spinner';

export const LicenseScanWrapper = () => {
  const { contextPath } = useContext(TenantContext);
  const { getConfiguration, getUserDriverLicense, getUserDocumentByType } = useCustomerAPI();

  const [params] = useSearchParams();
  const [orderId] = useState(params.get('orderId'));

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [idScanLicenseKey, setIdScanLicenseKey] = useState<string>();
  const [userLicenseDetails, setUserLicenseDetails] = useState<UserDriverLic | undefined>();
  const [userLicenseFile, setUserLicenseFile] = useState<UserDocument>();
  const [licenseScanRequired, setLicenseScanRequired] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);

    getConfiguration('CONF_IDSCAN_LICENSE_KEY')
      .then((config) => {
        if (config) {
          setIdScanLicenseKey(config?.value);
        } else {
          redirectToOrder();
        }
      })
      .catch(() => {
        toast('Failed to get configuration', 'error');
        redirectToOrder();
      });

    fetchLicenseDetails()
      .then(() => setIsLoading(false))
      .catch(() => {
        toast('Failed to get license details', 'error');
        setIsLoading(false);
      });
  }, []);

  const fetchLicenseDetails = async () => {
    const [licenseDetails, licenseFile] = await Promise.all([
      getUserDriverLicense(),
      getUserDocumentByType(UserDocumentType.License),
    ]);

    setLicenseScanRequired(licenseDetails === undefined || licenseDetails.expirationDate < DateTime.local());

    setUserLicenseDetails(licenseDetails);
    setUserLicenseFile(licenseFile);
  };

  const onSubmit = () => {
    setIsLoading(true);

    fetchLicenseDetails()
      .then(() => setIsLoading(false))
      .catch(() => {
        toast('Failed to get license details', 'error');
        setIsLoading(false);
      });
  };

  const redirectToOrder = () => {
    if (orderId) {
      window.location.href = `${window.location.origin}/${contextPath}/account/view_orders/${orderId}`;
    } else {
      window.location.href = `${window.location.origin}/${contextPath}/account/profile_settings`;
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <div>
      {idScanLicenseKey && licenseScanRequired ? (
        <>
          <h1 className="text-2xl font-semibold text-grey-800 text-center">Scan Driver's License</h1>
          <LicenseScan idScanLicenseKey={idScanLicenseKey} onSubmit={onSubmit} />
        </>
      ) : (
        <Card className="max-w-md bg-grey-25 !rounded-lg p-3 flex flex-col gap-2 justify-content-center">
          <img src="/images/success-icon.svg" className="max-w-[100px] ml-auto mr-auto" />
          <h1 className="text-2xl font-semibold text-grey-800 text-center">License Scan Completed</h1>

          {userLicenseDetails && (
            <>
              <h3 className="text-lg font-semibold text-grey-800 text-center mt-4">Driver's License Details</h3>

              <LicenseDetails userLicenseDetails={userLicenseDetails} userLicenseFile={userLicenseFile} />
            </>
          )}

          <div className="flex justify-content-center pt-3 mt-4">
            <EcommerceButton buttonStyle="primary" onClick={redirectToOrder}>
              <Typography type="text-md" weight="medium">
                {orderId ? 'Go to Order' : 'Go to Account'}
              </Typography>
            </EcommerceButton>
          </div>
        </Card>
      )}
    </div>
  );
};
